import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Flex, Grid } from '@chatterbug/aaron'
import { Button } from '@chatterbug/theme'
import { withTheme } from 'styled-components'
import { t } from 'src/lib/i18n-react'
import routes from 'src/lib/rails-routes/rails-routes'
import LiveDate from 'src/components/LiveDate'
import { getTimezone } from 'src/config/globals'
import { colors } from '@chatterbug/theme/tokens'

import UpcomingLessonIcon from 'src/ui/UpcomingLessonIcon/UpcomingLessonIcon'
import CoachMark from 'src/ui/CoachMark/CoachMark'
import DateUtil from 'src/lib/date-util'
import { getLanguageName } from 'lib/util'

import Countdown from '../Countdown/Countdown'
import UnitName from '../UnitName/UnitName'

import LessonDetailSkeleton from './LessonDetailSkeleton'
import reviewIcon from './assets/review.svg'

const COUNTDOWN_THRESHOLD = 2 * 60 // Two minutes
const JOIN_THRESHOLD = 2 * 60 // Two minutes

const SPOTLIGHT_SX = {
  // use `px`
  borderRadius: '40px',
}

const LessonDetail = ({
  plannedUnits,
  startTime,
  startsInSeconds,
  joinUrl,
  previewUrl,
  lessonLanguageCode,
  l2Code,
  plannerType,
  theme,
}) => {
  const firstPlannedUnit = plannedUnits?.[0]
  const multipleUnitsPresent = plannedUnits?.length > 1
  const secondPlannedUnit = multipleUnitsPresent && plannedUnits?.[1]
  const isReview = plannerType === 'review'
  const [canJoinLesson, setCanJoinLesson] = useState(false)
  const [showCountdown, setShowCountdown] = useState(false)
  const joinLessonTime = useRef()
  const showCountdownTimer = useRef()
  const timezone = getTimezone()
  const canStudyFlashcards = !canJoinLesson && !isReview && firstPlannedUnit

  useEffect(() => {
    clearTimeout(joinLessonTime.current)
    clearTimeout(showCountdownTimer.current)

    if (startsInSeconds <= JOIN_THRESHOLD) {
      setCanJoinLesson(true)
    }
    if (startsInSeconds > JOIN_THRESHOLD) {
      joinLessonTime.current = setTimeout(
        () => setCanJoinLesson(true),
        (startsInSeconds - JOIN_THRESHOLD) * 1000
      )
    }

    if (startsInSeconds <= COUNTDOWN_THRESHOLD) {
      setShowCountdown(true)
    }
    if (startsInSeconds > COUNTDOWN_THRESHOLD) {
      showCountdownTimer.current = setTimeout(
        () => setShowCountdown(true),
        (startsInSeconds - COUNTDOWN_THRESHOLD) * 1000
      )
    }
    return () => {
      clearTimeout(joinLessonTime.current)
      clearTimeout(showCountdownTimer.current)
    }
  }, [startsInSeconds])

  const previewButton = (
    <Button
      as="link"
      href={previewUrl}
      kind="secondary"
      presetColor="white"
      bg="dark"
      style={{ width: '100%' }}
      target={l2Code !== lessonLanguageCode ? '_blank' : undefined}
    >
      {t('Preview exercises')}
    </Button>
  )

  return (
    <LessonDetailSkeleton
      lessonSection={
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          {/* unit icons, maximum 2 */}
          <Flex>
            <UpcomingLessonIcon
              iconUrl={isReview ? reviewIcon : firstPlannedUnit?.iconUrl}
              lessonLanguageCode={lessonLanguageCode}
              unitUrl={
                firstPlannedUnit &&
                routes.advanced_unit(l2Code, firstPlannedUnit.id)
              }
              altText={firstPlannedUnit?.name || ''}
              showFlag={!multipleUnitsPresent}
            />
            {multipleUnitsPresent && (
              <Box ml={`-${theme.space['4x']}`}>
                <UpcomingLessonIcon
                  iconUrl={secondPlannedUnit.iconUrl}
                  lessonLanguageCode={lessonLanguageCode}
                  unitUrl={routes.advanced_unit(l2Code, secondPlannedUnit.id)}
                  altText={secondPlannedUnit.name || ''}
                  showFlag
                />
              </Box>
            )}
          </Flex>
          {plannedUnits?.length !== 0 && (
            <Box as="p" color="white100" variant="text.tinyTitle">
              {t('%{languageName} Live Lesson focus', {
                languageName: getLanguageName(lessonLanguageCode),
              })}
            </Box>
          )}
          {(isReview || firstPlannedUnit?.name) && (
            <Box
              as="p"
              sx={{
                color: 'white100',
                maxWidth: '100%',
                textAlign: 'center',
                // https://css-tricks.com/almanac/properties/l/line-clamp/
                display: '-webkit-box',
                '-webkit-line-clamp': '2',
                '-webkit-box-orient': 'vertical',
                overflow: 'hidden',
              }}
            >
              {/* name of the first unit */}
              <UnitName
                name={isReview ? t('Review') : firstPlannedUnit?.name}
                url={
                  firstPlannedUnit &&
                  routes.advanced_unit(l2Code, firstPlannedUnit.id)
                }
                shouldBeUnderlined={!isReview}
              />

              {/* name of the second unit */}
              {multipleUnitsPresent && (
                <>
                  <Box
                    as="span"
                    color="white100"
                    variant="text.h4"
                    mx="1x"
                    mt="0,5x"
                  >
                    {t('and')}
                  </Box>
                  <UnitName
                    name={secondPlannedUnit.name}
                    url={routes.advanced_unit(l2Code, secondPlannedUnit.id)}
                  />
                </>
              )}
            </Box>
          )}
        </Flex>
      }
      timeSection={
        <Grid
          gridGap="4x"
          columns={1}
          alignItems="center"
          justifyContent={{ _: 'center', tablet: 'space-between' }}
        >
          <Flex flexDirection="column" alignItems="center">
            {showCountdown ? (
              <Box
                as="p"
                display={{ _: 'block', tablet: 'flex' }}
                alignItems="center"
                textAlign="center"
              >
                <Box color="white100" as="h3" variant="text.h3" mr="1x">
                  {t('Starting in')}
                </Box>
                <Countdown toTime={startTime} />
              </Box>
            ) : (
              <Flex alignItems="center" textAlign="center">
                <Box as="h3" variant="text.h3" color="white100">
                  {DateUtil.calendarDate(startTime)}
                  <LiveDate
                    date={startTime}
                    format="cb_hmma"
                    hasTitle
                    spacedMeridiem
                    timezone={timezone}
                  />
                </Box>
              </Flex>
            )}
          </Flex>
          <Grid
            gap="2x"
            columns={{
              _: '1fr',
              tablet: `repeat(${
                canStudyFlashcards || canJoinLesson ? 2 : 1
              }, minmax(162px, auto))`,
            }}
            justifyContent={{ tablet: 'center' }}
          >
            {canStudyFlashcards && (
              <Button
                as="link"
                kind="secondary"
                presetColor="white"
                bg="dark"
                // if there is more than one unit and more than 50% of the first unit was seen, then go to card study of the second unit
                href={routes.cards_for_lesson(
                  l2Code,
                  multipleUnitsPresent &&
                    firstPlannedUnit?.progress?.percentOfExercisesSeen > 50
                    ? secondPlannedUnit.gid
                    : firstPlannedUnit?.gid
                )}
                target={l2Code !== lessonLanguageCode ? '_blank' : undefined}
              >
                {t('Study Flashcards')}
              </Button>
            )}
            <CoachMark
              disabled={canJoinLesson}
              name="coach_mark:preview_live_lesson"
              title={t('Preview your Lesson')}
              description={t(
                'Have a look at your upcoming exercises and learn more about your tutor.'
              )}
              align="above"
              spotlightSx={SPOTLIGHT_SX}
            >
              {previewButton}
            </CoachMark>
            {canJoinLesson && (
              <Button
                as="link"
                kind="primary"
                presetColor="white"
                bg="dark"
                href={joinUrl}
                target={l2Code !== lessonLanguageCode ? '_blank' : undefined}
                style={{ color: colors.blue42 }}
              >
                {t('Join Lesson')}
              </Button>
            )}
          </Grid>
        </Grid>
      }
    />
  )
}

LessonDetail.propTypes = {
  plannedUnits: PropTypes.object,
  startTime: PropTypes.string.isRequired,
  startsInSeconds: PropTypes.number.isRequired,
  joinUrl: PropTypes.string,
  previewUrl: PropTypes.string,
  lessonLanguageCode: PropTypes.string,
  l2Code: PropTypes.string.isRequired,
  plannerType: PropTypes.string.isRequired,
}
export default withTheme(LessonDetail)
